import { collection, getDocs, query, where, Timestamp, DocumentData } from 'firebase/firestore';
import { db } from '../firebase';
import { GLAccount } from './glAccountServices';
import { GLTransaction, GLTransactionEntry } from './glTransactionServices';

export interface TrialBalanceItem {
  accountId: string;
  accountNumber: string;
  accountName: string;
  debitBalance: number;
  creditBalance: number;
  netBalance: number;
}

export interface TrialBalanceData {
  items: TrialBalanceItem[];
  totalDebit: number;
  totalCredit: number;
  asOfDate: Date;
  startDate?: Date;
  endDate?: Date;
}

/**
 * Calculates the trial balance for all GL accounts
 * @param startDate Optional start date for filtering transactions
 * @param endDate Optional end date for filtering transactions
 * @returns Promise with TrialBalanceData
 */
export const generateTrialBalance = async (
  startDate?: Date,
  endDate?: Date
): Promise<TrialBalanceData> => {
  try {
    // Step 1: Get all GL accounts
    const accountsSnapshot = await getDocs(collection(db, 'glAccounts'));
    const accounts: GLAccount[] = [];
    accountsSnapshot.forEach((doc) => {
      accounts.push({ id: doc.id, ...doc.data() } as GLAccount);
    });

    // Step 2: Get all transactions within date range if specified
    let transactionsQuery;
    
    if (startDate || endDate) {
      const filters = [];
      
      if (startDate) {
        filters.push(where('transactionDate', '>=', Timestamp.fromDate(startDate)));
      }
      
      if (endDate) {
        // Add one day to include transactions on the end date
        const nextDay = new Date(endDate);
        nextDay.setDate(nextDay.getDate() + 1);
        filters.push(where('transactionDate', '<', Timestamp.fromDate(nextDay)));
      }
      
      transactionsQuery = query(collection(db, 'glTransactions'), ...filters);
    } else {
      transactionsQuery = collection(db, 'glTransactions');
    }
    
    const transactionsSnapshot = await getDocs(transactionsQuery);
    const transactions: GLTransaction[] = [];
    
    transactionsSnapshot.forEach((doc) => {
      const data = doc.data();
      const transaction = {
        id: doc.id,
        ...data,
        transactionDate: data.transactionDate instanceof Timestamp 
          ? data.transactionDate.toDate() 
          : data.transactionDate,
        entries: data.entries || []
      } as GLTransaction;
      
      transactions.push(transaction);
    });

    // Step 3: Calculate balances for each account
    const accountBalances: Record<string, { debit: number; credit: number }> = {};
    
    // Initialize all accounts with zero balances
    accounts.forEach(account => {
      if (account.id) {
        accountBalances[account.id] = { debit: 0, credit: 0 };
      }
    });
    
    // Process all transaction entries
    transactions.forEach(transaction => {
      transaction.entries.forEach((entry: GLTransactionEntry) => {
        if (!entry.accountId) return;
        
        if (!accountBalances[entry.accountId]) {
          accountBalances[entry.accountId] = { debit: 0, credit: 0 };
        }
        
        accountBalances[entry.accountId].debit += entry.debit || 0;
        accountBalances[entry.accountId].credit += entry.credit || 0;
      });
    });

    // Step 4: Create trial balance items
    const trialBalanceItems = accounts
      .filter(account => {
        // Skip accounts without an ID
        if (!account.id) return false;
        
        const balance = accountBalances[account.id];
        // Only include accounts with non-zero balances
        return balance && (balance.debit > 0 || balance.credit > 0);
      })
      .map(account => {
        // We've already filtered out accounts without an ID
        const accountId = account.id as string;
        const balance = accountBalances[accountId];
        const netBalance = balance.debit - balance.credit;
        
        return {
          accountId,
          accountNumber: account.accountNumber,
          accountName: account.accountName,
          debitBalance: netBalance > 0 ? netBalance : 0,
          creditBalance: netBalance < 0 ? Math.abs(netBalance) : 0,
          netBalance
        };
      })
      .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));

    // Step 5: Calculate totals
    const totalDebit = trialBalanceItems.reduce((sum, item) => sum + item.debitBalance, 0);
    const totalCredit = trialBalanceItems.reduce((sum, item) => sum + item.creditBalance, 0);

    return {
      items: trialBalanceItems,
      totalDebit,
      totalCredit,
      asOfDate: endDate || new Date(),
      startDate,
      endDate
    };
  } catch (error) {
    console.error('Error generating trial balance:', error);
    throw error;
  }
};
